import { useEffect, useState } from 'react';
import moment from 'moment';

/**
 * Forces a re-render exactly when the soonest of the given expiry timestamps
 * passes, then reschedules for the next-soonest, and so on. Existing
 * `moment(expiresAt).isAfter(moment())` eligibility checks only run on
 * render - this exists purely to make sure one runs right when that
 * comparison flips, instead of waiting on an unrelated re-render.
 *
 * Takes an array (rather than being called once per value) so a caller with
 * a dynamic-length list can use a single hook call regardless of how many
 * entries it's watching - calling a hook inside a .map() would vary the hook
 * count across renders and break the Rules of Hooks.
 */
const useExpiryTick = (expiresAtValues) => {
  const [, forceRerender] = useState(0);

  // A plain join of the values (rather than the array reference or a
  // JSON.stringify) so the effect only resets when the actual set of
  // timestamps changes, not on every render where the caller passes a fresh
  // array literal.
  const key = (expiresAtValues || []).filter(Boolean).join('|');

  useEffect(() => {
    if (!key) return undefined;

    let timeoutId;

    // Deliberately NOT memoized on `key` alone - "soonest still-future value"
    // depends on the current wall-clock time, not just the input values, so
    // it has to be recomputed on every fire rather than cached once per key.
    const scheduleNext = () => {
      const now = moment();
      const soonestMs = key
        .split('|')
        .map((value) => moment(value).diff(now))
        .filter((ms) => ms > 0)
        .reduce((min, ms) => (min === null || ms < min ? ms : min), null);

      if (soonestMs === null) return;

      // +1ms: consumers gate eligibility with `isBefore`/`isAfter`, which are
      // strict - firing at exactly soonestMs would land the re-render at the
      // same instant as expiresAt, where isBefore(now) is still false (equal,
      // not before). One tick past it guarantees the comparison has actually
      // flipped by the time the re-render runs.
      timeoutId = setTimeout(() => {
        forceRerender((tick) => tick + 1);
        scheduleNext();
      }, soonestMs + 1);
    };

    scheduleNext();

    return () => clearTimeout(timeoutId);
  }, [key]);
};

export default useExpiryTick;
